import { useQuery, useQueryClient } from 'react-query';

import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import type { ApiSingleUseCardDetails } from 'modules/single-use-cards/models/ApiSingleUseCardDetails';
import { type QueryError } from 'src/core/api/queryError';
import type { QueryState } from 'src/core/api/queryState';
import { getSinglePurchaseCardDetails } from 'src/core/modules/cards/api/getSinglePurchaseCardDetails';

export const singleUseCardQueryKey = (requestId: string) => [
  'singleUseCard',
  { requestId },
];

export const useSingleUseCardQuery = (
  requestId: string,
): QueryState<ApiSingleUseCardDetails> => {
  const companyId = useCompanyId();

  const result = useQuery(
    singleUseCardQueryKey(requestId),
    async (): Promise<ApiSingleUseCardDetails> => {
      return getSinglePurchaseCardDetails({
        companyId,
        requestId,
      });
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  if (result.status === 'loading') {
    return { status: 'loading' };
  }
  if (result.status === 'error') {
    return { status: 'error', error: result.error as QueryError<unknown> };
  }
  return { status: 'success', data: result.data as ApiSingleUseCardDetails };
};

export const useInvalidateSingleUseCardQuery = () => {
  const queryClient = useQueryClient();
  return (requestId: string) =>
    queryClient.invalidateQueries(singleUseCardQueryKey(requestId));
};

export const useInvalidateSingleUseCardsListQuery = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries('singlePurchaseCards');
};
